/* Copyright (c) 2025 Xperi. All Rights Reserved.  */

import {
    Configuration,
    ConfigurationParameters,
    MayAlsoLikeCollectionsApi,
    MayAlsoLikeCollectionsGetRequest,
    MayAlsoLikeContentsApi,
    MayAlsoLikeContentsGetRequest,
    MayAlsoLikeItems,
} from 'client-code/feeditems-service';

import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IFeedItemsServiceRepository } from '../interfaces';
import { Log } from '../logging';
import { TServicesParams } from '../types';
import { UniqueIdentifier } from '../utils';

/**
 * @inheritDoc
 */
export class FeedItemsServiceRepositoryImpl implements IFeedItemsServiceRepository {
    private static readonly TAG = 'FeedItemsServiceRepositoryImpl';

    private mParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mParams = params;
    }

    // eslint-disable-next-line class-methods-use-this
    private buildCommonHeaders(featureArea: string): CommonHeaders {
        return new CommonHeaders(featureArea);
    }

    private getConfigurationParameters(commonHeaders): ConfigurationParameters {
        return {
            apiKey: this.mParams.accessToken,
            basePath: this.mParams.basePath,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: commonHeaders,
            },
        };
    }

    private getCommonRequestParams(commonHeaders: CommonHeaders['headers']) {
        return {
            applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
            applicationVersion: commonHeaders.ApplicationVersion,
            bodyId: this.mParams.bodyId,
            applicationName: commonHeaders.ApplicationName,
            deviceType: commonHeaders.DeviceType,
            productName: commonHeaders.ProductName,
            acceptLanguage: commonHeaders['Accept-Language'],
            xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
            whiny: this.mParams.whiny || 'false',
        };
    }

    public async contentMayAlsoLikeGet(
        contentId: string,
        offset?: number,
        count?: number,
        includeAdult?: boolean,
        includeIpVod?: boolean,
        includeSportsPass?: boolean,
        providerPartnerId?: string,
    ): Promise<MayAlsoLikeItems> {
        try {
            const commonHeaders = this.buildCommonHeaders('FeedItemsService').headers;
            const config = this.getConfigurationParameters(commonHeaders);
            const configuration = new Configuration(config);
            const mayAlsoLikeContents = new MayAlsoLikeContentsApi(configuration);

            const mayAlsoLikeContentRequestParameters: MayAlsoLikeContentsGetRequest = {
                contentId,
                offset,
                count,
                includeAdult,
                includeIpVod,
                includeSportsPass,
                providerPartnerId,
                contentType: commonHeaders['Content-Type'],
                ...this.getCommonRequestParams(commonHeaders),
            };

            const response = await mayAlsoLikeContents.mayAlsoLikeContentsGet(
                mayAlsoLikeContentRequestParameters,
            );
            return response.data;
        } catch (error: any) {
            Log.e(FeedItemsServiceRepositoryImpl.TAG, 'contentMayAlsoLikeGet', error.message, {
                contentId,
                count,
                offset,
                includeAdult,
                includeIpVod,
                includeSportsPass,
                providerPartnerId,
            });
            throw error;
        }
    }

    public async collectionMayAlsoLikeGet(
        collectionId: string,
        offset?: number,
        count?: number,
        includeAdult?: boolean,
        includeIpVod?: boolean,
        includeSportsPass?: boolean,
        providerPartnerId?: string,
        isSeries?: boolean,
    ): Promise<MayAlsoLikeItems> {
        try {
            const commonHeaders = this.buildCommonHeaders('FeedItemsService').headers;
            const config = this.getConfigurationParameters(commonHeaders);
            const configuration = new Configuration(config);
            const mayAlsoLikeCollection = new MayAlsoLikeCollectionsApi(configuration);

            const mayAlsoLikeContentRequestParameters: MayAlsoLikeCollectionsGetRequest = {
                collectionId,
                offset,
                count,
                includeAdult,
                includeIpVod,
                includeSportsPass,
                providerPartnerId,
                isSeries,
                contentType: commonHeaders['Content-Type'],
                ...this.getCommonRequestParams(commonHeaders),
            };

            const response = await mayAlsoLikeCollection.mayAlsoLikeCollectionsGet(
                mayAlsoLikeContentRequestParameters,
            );
            return response.data;
        } catch (error: any) {
            Log.e(FeedItemsServiceRepositoryImpl.TAG, 'collectionMayAlsoLikeGet', error.message, {
                collectionId,
                offset,
                count,
                includeAdult,
                includeIpVod,
                includeSportsPass,
                providerPartnerId,
                isSeries,
            });
            throw error;
        }
    }
}
